import React from "react";
import { useTranslation } from "react-i18next";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { IoArrowBack, IoArrowForward } from "react-icons/io5";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Modal,
  Typography,
  Stack,
  Paper,
  useColorScheme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useScreenSize } from "../../hooks/useScreenSize";
import {
  actionBarButtonPrimaryDarkStyle,
  actionBarButtonPrimaryLightStyle
} from "../../styles";

interface TourStepProps {
  title: string;
  content: string;
  image?: string;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSkip: () => void;
}

const TourStep: React.FC<TourStepProps> = ({
  title,
  content,
  image,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onClose,
  onSkip
}) => {
  const { mode: themeVariant } = useColorScheme();
  const { t } = useTranslation();
  const { isSmallScreen } = useScreenSize();

  return (
    <Modal
      open
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(0px)"
          }
        }
      }}
      sx={{
        zIndex: 19999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Paper
        elevation={0}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-step-title"
        sx={{
          maxWidth: "600px",
          width: "90%",
          borderRadius: 2,
          "&:focus:not(:focus-visible)": { outline: "none" }
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center">
          <Card
            sx={{
              width: "100%",
              px: 1,
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <CardHeader
              id="tour-step-title"
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <IoIosHelpCircleOutline size="25px" />
                  <Typography variant="h6">{title}</Typography>
                </Stack>
              }
              action={
                <IconButton onClick={onClose} aria-label="close">
                  <CloseIcon />
                </IconButton>
              }
            />

            {image && (
              <Stack
                sx={{ mx: 2.5 }}
                tabIndex={0}
                role="region"
                aria-label={t("ROOSTER.PUBLIC-APP.TOUR.IMAGE_PREVIEW", {
                  defaultValue: "Tutorial preview"
                })}
                data-testid="tour-step-image-container"
              >
                <CardMedia
                  component="img"
                  image={image}
                  alt={title}
                  sx={{
                    maxHeight: "300px",
                    objectFit: "contain",
                    border: "1px solid",
                    borderRadius: "4px",
                    borderColor: "divider"
                  }}
                />
              </Stack>
            )}

            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="body1" fontSize="16px">
                {content}
              </Typography>
            </CardContent>

            <CardActions
              sx={{ justifyContent: "space-between", p: 2.5, pt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("ROOSTER.PUBLIC-APP.STEP_COUNT", {
                  defaultValue: "Step {{current}} of {{total}}",
                  current: currentStep,
                  total: totalSteps
                })}
              </Typography>

              <Stack direction="row" spacing={1}>
                {!isSmallScreen && (
                  <Button
                    size="large"
                    color="inherit"
                    onClick={onSkip}
                    sx={{
                      ...(themeVariant === "light"
                        ? {
                            ...actionBarButtonPrimaryLightStyle("text"),
                            color: "#375990"
                          }
                        : actionBarButtonPrimaryDarkStyle("text")),
                      backgroundColor: "transparent"
                    }}
                  >
                    {t("ROOSTER.PUBLIC-APP.SKIP-TOUR", {
                      defaultValue: "Skip"
                    })}
                  </Button>
                )}

                {currentStep > 1 && (
                  <Button
                    size="large"
                    variant="text"
                    onClick={onPrev}
                    sx={{
                      ...(themeVariant === "light"
                        ? {
                            ...actionBarButtonPrimaryLightStyle("text"),
                            color: "#375990"
                          }
                        : actionBarButtonPrimaryDarkStyle("text")),
                      backgroundColor: "transparent"
                    }}
                  >
                    <IoArrowBack /> &nbsp;
                    {t("ROOSTER.PUBLIC-APP.PREVIOUS", {
                      defaultValue: "Previous"
                    })}
                  </Button>
                )}

                <Button
                  size="large"
                  variant="outlined"
                  color="primary"
                  onClick={onNext}
                  sx={{
                    ...(themeVariant === "light"
                      ? actionBarButtonPrimaryLightStyle("contained")
                      : actionBarButtonPrimaryDarkStyle("contained"))
                  }}
                >
                  {currentStep === totalSteps
                    ? t("ROOSTER.PUBLIC-APP.FINISH", {
                        defaultValue: "Finish"
                      })
                    : t("ROOSTER.PUBLIC-APP.NEXT", {
                        defaultValue: "Next"
                      })}
                  &nbsp;
                  {currentStep !== totalSteps && <IoArrowForward />}
                </Button>
              </Stack>
            </CardActions>
          </Card>
        </Stack>
      </Paper>
    </Modal>
  );
};

export default TourStep;
